import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import AbonnementBanner from './AbonnementBanner';
import OfflineBanner from './OfflineBanner';
import NotificationBell from './NotificationBell';
import api, { getBoutiqueVue, setBoutiqueVue } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';
import FooterMentions from '../common/FooterMentions';
import { Store } from 'lucide-react';

// ── Sélecteur global de boutique (propriétaire / admin) ──────────
// Visible uniquement si le commerce a PLUSIEURS boutiques actives,
// ET seulement sur les pages dont les données sont réellement
// filtrables par boutique. Inutile sur Paramètres, Abonnement,
// Activité, Produits (catalogue commun), Clients, Fournisseurs…
const PAGES_AVEC_VUE_BOUTIQUE = [
  '/dashboard', '/ventes', '/caisse', '/commandes',
  '/commandes-client', '/rapports', '/stock', '/vendeurs',
  '/journal-ventes',
];

function SelecteurBoutiqueGlobal() {
  const { user } = useAuth();
  const location = useLocation();
  const [boutiques, setBoutiques] = useState([]);
  const [vue, setVue] = useState(getBoutiqueVue());
  const concerne = ['admin', 'proprietaire', 'consultation'].includes(user?.role);
  const pageConcernee = PAGES_AVEC_VUE_BOUTIQUE.some(
    p => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  useEffect(() => {
    if (!concerne) return;
    api.get('/boutiques')
      .then(r => {
        const actives = (r.data || []).filter(b => b.statut === 'actif');
        setBoutiques(actives);
        // Sécurité : si la boutique mémorisée n'existe plus (ou vient d'un
        // autre commerce après changement de session), on revient à « toutes ».
        const memorisee = getBoutiqueVue();
        if (memorisee && !actives.some(b => b.id === memorisee)) {
          setBoutiqueVue(null);
          setVue(null);
        }
      })
      .catch(() => setBoutiques([]));
  }, [concerne]);

  // Masqué si : commerce mono-boutique, rôle non concerné, ou page dont
  // les données ne dépendent pas d'une boutique.
  if (!concerne || boutiques.length < 2 || !pageConcernee) return null;

  const changer = (val) => {
    setBoutiqueVue(val || null);
    setVue(val || null);
    // Recharge la page courante pour que toutes les données affichées
    // (déjà chargées) reflètent la nouvelle vue.
    window.location.reload();
  };

  const nomBoutiqueActive = boutiques.find(b => b.id === vue)?.nom;
  const pilule = (actif) =>
    `flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer border-2 ${
      actif
        ? 'bg-yellow-500 text-black border-yellow-400 shadow-lg shadow-yellow-500/30 scale-105'
        : 'bg-gray-800/80 text-gray-300 border-gray-700 hover:border-yellow-500/60 hover:text-white'
    }`;

  return (
    <div className="px-4 md:px-6 py-3 bg-gradient-to-r from-yellow-500/10 via-gray-900 to-gray-900 border-b-2 border-yellow-500/40">
      <div className="flex items-center gap-2 mb-2">
        <Store size={16} className="text-yellow-400 flex-shrink-0" />
        <span className="text-sm font-bold text-yellow-400 uppercase tracking-wide">
          Boutique affichée
        </span>
        <span className="text-xs text-gray-400 hidden md:inline">
          — ce choix s'applique à toutes les pages (ventes, caisse, rapports…)
        </span>
      </div>

      {boutiques.length <= 4 ? (
        // Peu de boutiques : boutons piluled bien visibles
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => changer('')} className={pilule(!vue)}>
            🏬 Toutes ensemble
          </button>
          {boutiques.map(b => (
            <button key={b.id} onClick={() => changer(b.id)} className={pilule(vue === b.id)}>
              {b.nom}{b.ville ? <span className="font-normal opacity-70"> · {b.ville}</span> : null}
            </button>
          ))}
        </div>
      ) : (
        // Beaucoup de boutiques : liste déroulante, mais bien mise en valeur
        <select
          value={vue || ''}
          onChange={e => changer(e.target.value)}
          className="bg-gray-800 text-white border-2 border-yellow-500/60 rounded-xl px-4 py-2.5 text-sm font-semibold focus:outline-none focus:border-yellow-400 shadow-lg"
        >
          <option value="">🏬 Toutes les boutiques (ensemble)</option>
          {boutiques.map(b => (
            <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>
          ))}
        </select>
      )}

      {vue && (
        <p className="text-xs text-yellow-300/90 mt-2 flex items-center gap-1.5">
          ⚠️ Vue filtrée : seules les données de <strong>{nomBoutiqueActive}</strong> sont affichées partout.
        </p>
      )}
    </div>
  );
}

// Pages où l'admin est en lecture seule (toutes sauf paramètres + dashboard).
// L'admin peut consulter pour superviser, mais ne peut pas créer/modifier.
// (Produits & Fournisseurs ne sont PAS dans cette liste : l'admin peut
//  désormais y créer/éditer via le middleware `writeCatalog`.)
const PAGES_LECTURE_SEULE_ADMIN = [
  '/stock', '/vendeurs',
  '/ventes', '/commandes', '/caisse', '/rapports',
];

// Assombrit une couleur hex en mélangeant avec du noir (pour obtenir un
// fond de contenu plus sombre que la sidebar mais dans la même famille).
function assombrir(hex, ratio = 0.6) {
  if (!hex) return '#0f172a';
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return hex;
  const r = Math.round(parseInt(h.substring(0, 2), 16) * (1 - ratio));
  const g = Math.round(parseInt(h.substring(2, 4), 16) * (1 - ratio));
  const b = Math.round(parseInt(h.substring(4, 6), 16) * (1 - ratio));
  return `rgb(${r}, ${g}, ${b})`;
}

export default function Layout({ children }) {
  const [rupturesStock, setRupturesStock] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAdmin, isProprietaire, isConsultation, couleurFond } = useAuth();
  const { pathname } = useLocation();
  const isMobile = useIsMobile(1024);

  // Le fond du contenu (cadran de droite) est une version assombrie de la
  // couleur du commerce — assez sombre pour rester lisible avec les cards
  // sombres existantes, mais teinté pour s'harmoniser avec la sidebar.
  const contentBg = assombrir(couleurFond, 0.7);

  // Bannière lecture seule : admin sur une page business
  const enLectureSeule = isAdmin && PAGES_LECTURE_SEULE_ADMIN.some(p => pathname.startsWith(p));

  // Fermer le drawer à chaque changement de route + verrouiller le scroll quand ouvert
  useEffect(() => { setMobileMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileMenuOpen]);

  // Charger le nombre de ruptures de stock pour le badge sidebar
  useEffect(() => {
    const fetchAlertes = () => {
      api.get('/dashboard/today')
        .then(r => setRupturesStock((r.data.rupturesStock || 0) + (r.data.alertesStock || 0)))
        .catch(() => {});
    };
    fetchAlertes();
    const interval = setInterval(fetchAlertes, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Padding bas mobile = hauteur bottom-nav (60px) + safe-area + petit espace
  // Desktop : aucun ajustement.
  const mainPaddingBottom = isMobile
    ? 'calc(env(safe-area-inset-bottom, 0px) + 76px)'
    : undefined;

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: contentBg }}>
      <Sidebar
        alertesStock={rupturesStock}
        mobileOpen={mobileMenuOpen}
        onMobileChange={setMobileMenuOpen}
        hideMobileToggle={isMobile}
      />
      <main className="lg:ml-64 min-h-screen flex flex-col" style={{ paddingBottom: mainPaddingBottom }}>
        {/* Barre de notifications (cloche) — propriétaire & admin uniquement */}
        {isProprietaire && (
          <div className="flex items-center justify-end px-4 md:px-6 pt-3">
            <NotificationBell />
          </div>
        )}
        {/* Sélecteur global de boutique (propriétaire/admin multi-boutiques) */}
        <SelecteurBoutiqueGlobal />
        {/* Bannière SaaS : avertissement abonnement (J-15, grâce, lecture seule) */}
        <AbonnementBanner />
        {/* Bannière offline + sync (L21) */}
        <OfflineBanner />
        {/* Contenu : prend tout l'espace disponible pour pousser le pied en bas */}
        <div className="p-4 md:p-6 lg:pt-6 pt-4 flex-1">
          {isConsultation && (
            <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-blue-900/30 border border-blue-700/50 rounded-xl text-sm text-blue-200">
              <ShieldAlert size={16} className="text-blue-400 flex-shrink-0" />
              <span>
                <strong>Mode consultation</strong> — accès en lecture seule à tout le commerce.
                Tu peux consulter, imprimer et exporter, mais aucune modification n'est possible.
              </span>
            </div>
          )}
          {enLectureSeule && (
            <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-red-900/30 border border-red-700/50 rounded-xl text-sm text-red-200">
              <ShieldAlert size={16} className="text-red-400 flex-shrink-0" />
              <span>
                <strong>Mode lecture seule (admin)</strong> — tu peux consulter mais
                pas modifier sur cette page. Pour éditer, utilise un compte propriétaire ou employé.
              </span>
            </div>
          )}
          {children}
        </div>
        {/* Pied de page : toujours en bas, même si la page est courte */}
        <footer className="mt-8 pt-4 px-4 md:px-6 pb-4 border-t border-gray-800/60">
          <FooterMentions variant="dark" />
        </footer>
      </main>
      {isMobile && <BottomNav onOpenMenu={() => setMobileMenuOpen(true)} />}
    </div>
  );
}
